// ── Report review section — mark-reviewed / approve, role-gated (Wave 7) ────
// POST /reports/:id/review { decision }. Per the backend contract:
//   - decision="reviewed": company_admin / coach (+ staff) may set reviewed_at on
//     reports they may READ (team_admin/company_readable). cohort_lead +
//     participant are denied outright (403).
//   - decision="approved": STAFF-ONLY (super_admin/admin). company_admin / coach
//     are denied approve (403).
// The capability layer (reports:mark-reviewed) gates the Mark-reviewed control;
// Approve is rendered ONLY for staff (isStaffRole). The toast fires ONLY after the
// backend confirms (post-confirm). A denied review never renders an enabled
// control — the backend stays authoritative and a UI control can never widen the
// role. The reviewed/approved state is read back from the report (reviewed_at /
// approved_at) so the section reflects server truth, not optimistic UI.

import { Alert, Badge, Button, Module, ModuleBody, ModuleHead, useToast } from "@vjal/ui";

import { CapabilityButton } from "../../../components/AccessState";
import { evaluateActionCapability } from "../../../domain/capabilities";
import { isStaffRole } from "../../../domain/sensitive-surfaces";
import { useReportMarkReviewedMutation } from "../../../hooks/useCoachingOrgAnalytics";
import type { CoachingArtifactDto } from "../../../services/coaching-dtos";
import type { CoachingRole } from "../../../domain/roles";
import { describeReportWriteError } from "../reports-write";

export function ReportReviewSection({ report, role }: { report: CoachingArtifactDto; role: CoachingRole }) {
  const toast = useToast();
  const reviewDecision = evaluateActionCapability(role, "reports:mark-reviewed");
  const markReviewed = useReportMarkReviewedMutation();
  const canApprove = isStaffRole(role);

  const reviewed = Boolean(report.reviewed_at);
  const approved = Boolean(report.approved_at);
  const writeError = describeReportWriteError(markReviewed.error);

  function runReview(decision: "reviewed" | "approved") {
    markReviewed.reset();
    markReviewed.mutate(
      { reportId: report.id, decision },
      {
        onSuccess: () => toast.success(decision === "approved" ? "Report approved" : "Report marked reviewed")
      }
    );
  }

  return (
    <Module span={4}>
      <ModuleHead title="Review" subtitle="Reviewing and approving follow your role." />
      <ModuleBody className="space-y-3" data-testid="report-review">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={reviewed ? "success" : "warning"}>{reviewed ? "Reviewed" : "Awaiting review"}</Badge>
          {approved ? <Badge variant="success">Approved</Badge> : null}
        </div>

        {/* Mark reviewed — capability-gated (cohort_lead/participant disabled). */}
        <CapabilityButton
          decision={reviewDecision}
          loading={markReviewed.isPending}
          onClick={() => runReview("reviewed")}
          data-testid="report-mark-reviewed-affordance"
        >
          {reviewed ? "Mark reviewed again" : "Mark reviewed"}
        </CapabilityButton>

        {/* Approve — STAFF-ONLY: never rendered for any non-staff role. */}
        {canApprove ? (
          <Button
            type="button"
            variant="secondary"
            loading={markReviewed.isPending}
            onClick={() => runReview("approved")}
            aria-label="Approve report"
            data-testid="report-approve-affordance"
          >
            {approved ? "Re-approve" : "Approve"}
          </Button>
        ) : null}

        {writeError ? <Alert variant={writeError.tone}>{writeError.message}</Alert> : null}
      </ModuleBody>
    </Module>
  );
}
